import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Award,
  MessageCircle,
  Star,
  Clock,
  Copy,
  Check,
  User,
} from "lucide-react";
import { alphaConversationsApi } from "../api/client";
import { fmtDate, fmtMs, langLabel } from "../utils/format";

// Distinct colours for each response label, aligned with the new aesthetic
const RESPONSE_STYLE = {
  A: { accent: "#C26C1B", bg: "#FDF7F2", border: "rgba(194, 108, 27, 0.2)" },
  B: { accent: "#4A6741", bg: "#F2F6F0", border: "rgba(74, 103, 65, 0.2)" },
  C: { accent: "#1A3A5C", bg: "#F0F4F8", border: "rgba(26, 58, 92, 0.2)" },
};

const DEFAULT_STYLE = {
  accent: "#847468",
  bg: "#F9F6F1",
  border: "var(--ink-200)",
};

const Skeleton = ({
  width = "100%",
  height = 20,
  borderRadius = 4,
  style = {},
}) => (
  <div
    className="skeleton-box"
    style={{
      display: "inline-block",
      width,
      height,
      borderRadius,
      ...style,
    }}
  />
);

function StarRating({ value, max = 5, color = "#C26C1B" }) {
  return (
    <span style={{ display: "inline-flex", gap: 2 }}>
      {Array.from({ length: max }).map((_, i) => (
        <Star
          key={i}
          size={14}
          fill={i < value ? "currentColor" : "none"}
          style={{ color: i < value ? color : "var(--ink-200)" }}
        />
      ))}
    </span>
  );
}

function DimensionBar({ label, value, accent = "var(--saffron)" }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 12,
          color: "var(--ink-700)",
          marginBottom: 4,
          fontWeight: 500,
        }}
      >
        <span>{label}</span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{value}%</span>
      </div>
      <div
        style={{
          height: 6,
          background: "rgba(0,0,0,0.06)",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${value}%`,
            background: accent,
            borderRadius: 3,
            transition: "width 0.5s ease",
          }}
        />
      </div>
    </div>
  );
}

// Group messages into turns: each user message + its assistant responses
function groupTurns(messages, evaluations) {
  const evalByUserMsgId = {};
  evaluations.forEach((e) => {
    evalByUserMsgId[String(e.user_message_id)] = e;
  });

  const turns = [];
  let i = 0;
  while (i < messages.length) {
    if (messages[i].role === "user") {
      const userMsg = messages[i];
      const responses = [];
      i++;
      while (i < messages.length && messages[i].role !== "user") {
        if (messages[i].role === "assistant") responses.push(messages[i]);
        i++;
      }
      const evaluation = evalByUserMsgId[String(userMsg._id)] || null;

      // Pair each response message with its evaluation entry
      const labeled = responses
        .map((msg) => {
          const evalData = evaluation
            ? evaluation.responses.find(
              (r) => String(r.message_id) === String(msg._id),
            )
            : null;
          const label =
            evalData?.label ||
            (msg.source_model || "")
              .replace(/^response-/i, "")
              .toUpperCase() ||
            "";
          return { msg, label, evalData };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

      turns.push({ userMsg, responses: labeled, evaluation });
    } else {
      i++;
    }
  }
  return turns;
}

export default function AlphaConversationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      alphaConversationsApi.detail(id),
      alphaConversationsApi.evaluations(id),
    ])
      .then(([detail, evalData]) => {
        if (cancelled) return;
        setData(detail);
        setEvaluations(evalData.evaluations || []);
      })
      .catch(
        (e) => !cancelled && setError(e?.response?.data?.error || e.message),
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading)
    return (
      <div style={{ width: "100%", paddingBottom: 64 }}>
        <style>{`
          @keyframes skeleton-loading {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
          .skeleton-box {
            background: linear-gradient(90deg, var(--ink-100, #f3f4f6) 25%, var(--ink-200, #e5e7eb) 50%, var(--ink-100, #f3f4f6) 75%);
            background-size: 200% 100%;
            animation: skeleton-loading 1.5s infinite linear;
          }
        `}</style>

        {/* Back link skeleton */}
        <Skeleton
          width={60}
          height={16}
          style={{ marginBottom: 28, display: "block" }}
        />

        {/* Header skeleton */}
        <div style={{ marginBottom: 32 }}>
          <Skeleton
            width={220}
            height={16}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Skeleton
            width="60%"
            height={40}
            style={{ marginBottom: 12, display: "block" }}
          />
          <div style={{ display: "flex", gap: 10 }}>
            <Skeleton width={80} height={24} borderRadius={12} />
            <Skeleton width={110} height={24} borderRadius={12} />
            <Skeleton width={90} height={24} borderRadius={12} />
            <Skeleton width={110} height={24} borderRadius={12} />
            <Skeleton width={70} height={24} borderRadius={12} />
          </div>
        </div>

        {/* Main Transcript Panel Wrapper Skeleton */}
        <div
          className="panel"
          style={{
            background: "#ffffff",
            borderRadius: 16,
            border: "1px solid var(--ink-100)",
            boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "24px 32px",
              background: "rgba(251, 246, 238, 0.3)",
              borderBottom: "1px solid var(--ink-100)",
            }}
          >
            <Skeleton width={200} height={28} />
          </div>
          <div style={{ padding: "32px" }}>
            {[...Array(2)].map((_, i) => (
              <div key={i} style={{ marginBottom: 48 }}>
                {/* Turn Divider Skeleton */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 20,
                  }}
                >
                  <Skeleton width={32} height={32} borderRadius="50%" />
                  <div style={{ flex: 1 }}>
                    <Skeleton
                      width="100%"
                      height={1}
                      style={{ display: "block" }}
                    />
                  </div>
                  <Skeleton width={100} height={14} />
                </div>

                {/* User Prompt Skeleton */}
                <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                  <Skeleton
                    width={40}
                    height={40}
                    borderRadius="50%"
                    style={{ flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0, maxWidth: "80%" }}>
                    <Skeleton
                      width={120}
                      height={14}
                      style={{ marginBottom: 8, display: "block" }}
                    />
                    <Skeleton
                      width="100%"
                      height={80}
                      borderRadius={16}
                      style={{ borderTopLeftRadius: 4, display: "block" }}
                    />
                  </div>
                </div>

                {/* Evaluation Summary Bar Skeleton */}
                <div style={{ marginBottom: 16 }}>
                  <Skeleton
                    width="100%"
                    height={44}
                    borderRadius={12}
                    style={{ display: "block" }}
                  />
                </div>

                {/* Responses Skeleton */}
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  {[...Array(2)].map((_, j) => (
                    <div
                      key={j}
                      style={{
                        border: "1px solid var(--ink-100)",
                        borderRadius: 16,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          padding: "12px 20px",
                          borderBottom: "1px solid var(--ink-100)",
                          background: "var(--paper-2)",
                          display: "flex",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <Skeleton width={28} height={28} borderRadius="50%" />
                        <Skeleton width={80} height={16} />
                      </div>
                      <div style={{ padding: "20px" }}>
                        <Skeleton width="100%" height={80} />
                      </div>
                      <div
                        style={{
                          padding: "16px 20px",
                          borderTop: "1px solid var(--ink-100)",
                          background: "var(--paper-2)",
                        }}
                      >
                        <Skeleton
                          width={150}
                          height={16}
                          style={{ marginBottom: 16, display: "block" }}
                        />
                        <div
                          style={{ display: "flex", gap: 24, marginBottom: 16 }}
                        >
                          <Skeleton width="30%" height={24} />
                          <Skeleton width="30%" height={24} />
                        </div>
                        <Skeleton
                          width="100%"
                          height={50}
                          borderRadius={8}
                          style={{ display: "block" }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  if (error)
    return (
      <div
        style={{
          padding: "16px 20px",
          borderRadius: 12,
          border: "1px solid var(--red)",
          background: "#fff1f2",
          color: "var(--red)",
          margin: "24px 0",
          display: "flex",
          alignItems: "center",
          gap: 12,
          fontWeight: 500,
        }}
      >
        {error}
      </div>
    );
  if (!data) return null;

  const { conversation: conv, messages = [] } = data;
  const turns = groupTurns(messages, evaluations);

  return (
    <div style={{ width: "100%", paddingBottom: 64 }}>
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: "var(--ink-500)",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12,
          fontWeight: 600,
          marginBottom: 28,
          padding: 0,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          transition: "color 0.2s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ink-900)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ink-500)")}
      >
        <ArrowLeft size={14} /> Back
      </button>

      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div
          className="t-eyebrow"
          style={{
            color: "var(--ink-500)",
            marginBottom: 8,
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
          }}
        >
          <span>Alpha Conversation ·</span>
          <code
            style={{
              fontFamily: "var(--mono)",
              fontSize: 11,
              background: "var(--ink-100)",
              padding: "2px 6px",
              borderRadius: 4,
              color: "var(--ink-600)",
              letterSpacing: "0",
              wordBreak: "break-all",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {conv.session_id}
            <button
              onClick={() => {
                navigator.clipboard.writeText(conv.session_id);
                setIsCopied(true);
                setTimeout(() => setIsCopied(false), 2000);
              }}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: isCopied ? "var(--green)" : "var(--ink-400)",
                display: "flex",
                alignItems: "center",
              }}
              title="Copy Session ID"
            >
              {isCopied ? <Check size={12} /> : <Copy size={12} />}
            </button>
          </code>
        </div>
        <h1
          className="t-display"
          style={{
            fontSize: 36,
            margin: "0 0 12px 0",
            color: "var(--ink-900)",
          }}
        >
          Evaluation Transcript ·{" "}
          <em style={{ color: "var(--saffron-deep)", fontStyle: "italic" }}>
            {fmtDate(conv.started_at, {
              hour: undefined,
              minute: undefined,
              hour12: undefined,
            })}
          </em>
        </h1>
        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            alignItems: "center",
            fontSize: 13,
            color: "var(--ink-600)",
            fontWeight: 500,
          }}
        >
          <span className="badge badge-outline" style={{ padding: "4px 10px" }}>
            {langLabel(conv.language)}
          </span>
          {/* <span className="badge badge-neutral" style={{ padding: "4px 10px" }}>
            <MessageCircle
              size={12}
              style={{ verticalAlign: "middle", marginRight: 4 }}
            />
            {conv.message_count} messages
          </span> */}
          {/* <span className="badge badge-neutral" style={{ padding: "4px 10px" }}>
            {turns.length} prompt{turns.length !== 1 ? "s" : ""}
          </span>
          <span className="badge badge-neutral" style={{ padding: "4px 10px" }}>
            {evaluations.length} evaluation{evaluations.length !== 1 ? "s" : ""}
          </span> */}
          {/* <span
            className={`badge ${conv.status === "active" ? "badge-green" : "badge-neutral"}`}
            style={{ padding: "4px 10px" }}
          >
            {conv.status}
          </span> */}
        </div>
      </div>

      {/* Main Transcript Panel Wrapper */}
      <div
        className="panel"
        style={{
          background: "#ffffff",
          borderRadius: 16,
          border: "1px solid var(--ink-100)",
          boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
          overflow: "hidden",
        }}
      >
        <div
          className="panel-header"
          style={{
            padding: "24px 32px",
            background: "rgba(251, 246, 238, 0.3)",
            borderBottom: "1px solid var(--ink-100)",
          }}
        >
          <div
            className="t-display"
            style={{ fontSize: 24, color: "var(--ink-900)" }}
          >
            Evaluation rounds
          </div>
        </div>
        <div style={{ padding: "32px" }}>
          {turns.length === 0 && (
            <div
              style={{
                padding: "48px 24px",
                textAlign: "center",
                color: "var(--ink-400)",
                fontFamily: "var(--serif)",
                fontStyle: "italic",
                fontSize: 16,
              }}
            >
              No messages in this conversation yet.
            </div>
          )}

          {turns.map((turn, ti) => {
            const isDevanagari =
              turn.userMsg.language?.startsWith("hi") ||
              turn.userMsg.language?.startsWith("mr");

            return (
              <div key={turn.userMsg._id} style={{ marginBottom: 48 }}>
                {/* Turn Divider */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 20,
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: "var(--ink-100)",
                      color: "var(--ink-700)",
                      fontSize: 14,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {ti + 1}
                  </div>
                  <div
                    style={{ flex: 1, height: 1, background: "var(--ink-100)" }}
                  />
                  <span
                    style={{
                      fontSize: 12,
                      color: "var(--ink-400)",
                      fontFamily: "var(--mono)",
                    }}
                  >
                    {fmtDate(turn.userMsg.createdAt)}
                  </span>
                </div>

                {/* User Prompt (Seeker) */}
                <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: "50%",
                      background: "var(--paper-2)",
                      border: "1px solid var(--ink-200)",
                      color: "var(--ink-700)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <User size={18} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, maxWidth: "80%" }}>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--ink-500)",
                        fontWeight: 500,
                        marginBottom: 8,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          fontWeight: 700,
                          color: "var(--ink-700)",
                        }}
                      >
                        Seeker Prompt
                      </span>
                    </div>
                    <div
                      style={{
                        background: "var(--paper-2)",
                        border: "1px solid var(--ink-200)",
                        borderRadius: 16,
                        borderTopLeftRadius: 4,
                        padding: "16px 20px",
                        fontFamily: isDevanagari
                          ? "var(--devanagari)"
                          : "var(--sans)",
                        fontSize: isDevanagari ? 16 : 15,
                        lineHeight: 1.6,
                        color: "var(--ink-900)",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        boxShadow: "0 2px 8px -2px rgba(0,0,0,0.02)",
                      }}
                    >
                      {turn.userMsg.content}
                    </div>
                  </div>
                </div>

                {/* Evaluation Summary Bar */}
                {turn.evaluation && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 16,
                      fontSize: 13,
                      color: "var(--ink-800)",
                      background: "var(--saffron-tint)",
                      padding: "12px 16px",
                      borderRadius: 12,
                      border: "1px solid rgba(194, 108, 27, 0.15)",
                    }}
                  >
                    <Award size={16} style={{ color: "var(--saffron-deep)" }} />
                    <span>
                      Evaluator preferred:{" "}
                      <strong
                        style={{
                          color:
                            RESPONSE_STYLE[turn.evaluation.preferred]?.accent ||
                            "var(--saffron-deep)",
                          fontSize: 14,
                        }}
                      >
                        Response {turn.evaluation.preferred}
                      </strong>
                    </span>
                    <span style={{ color: "var(--ink-300)" }}>|</span>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        color: "var(--ink-600)",
                      }}
                    >
                      <Clock size={12} /> Submitted{" "}
                      {fmtDate(turn.evaluation.submitted_at)}
                    </span>
                  </div>
                )}

                {/* Responses List */}
                {turn.responses.length > 0 ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 16,
                    }}
                  >
                    {turn.responses.map(({ msg, label, evalData }) => {
                      const style = RESPONSE_STYLE[label] || DEFAULT_STYLE;
                      const isPreferred = evalData?.is_preferred;

                      return (
                        <div
                          key={msg._id}
                          style={{
                            border: `1px solid ${isPreferred ? style.accent : style.border}`,
                            borderRadius: 16,
                            overflow: "hidden",
                            background: "#ffffff",
                            boxShadow: isPreferred
                              ? `0 4px 16px ${style.border}`
                              : "0 2px 8px -2px rgba(0,0,0,0.02)",
                            position: "relative",
                            transition: "all 0.2s ease",
                          }}
                        >
                          {/* Response Header */}
                          <div
                            style={{
                              padding: "12px 20px",
                              borderBottom: `1px solid ${style.border}`,
                              background: isPreferred
                                ? style.bg
                                : "var(--paper-2)",
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <span
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                background: style.accent,
                                color: "#fff",
                                fontWeight: 600,
                                fontSize: 14,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              {label}
                            </span>
                            <span
                              style={{
                                fontWeight: 600,
                                fontSize: 15,
                                color: "var(--ink-900)",
                              }}
                            >
                              Response {label}
                            </span>
                            {isPreferred && (
                              <span
                                style={{
                                  marginLeft: "auto",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: style.accent,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                }}
                              >
                                <Award size={14} /> Preferred
                              </span>
                            )}
                          </div>

                          {/* Response Content */}
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <div
                              style={{
                                padding: "20px",
                                fontFamily: isDevanagari
                                  ? "var(--devanagari)"
                                  : "var(--sans)",
                                fontSize: isDevanagari ? 16 : 15,
                                lineHeight: 1.6,
                                color: "var(--ink-900)",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                minHeight: 80,
                              }}
                            >
                              {msg.content}
                            </div>

                            {/* Evaluation Panel Footer */}
                            {evalData && (
                              <div
                                style={{
                                  padding: "16px 20px",
                                  borderTop: `1px solid ${style.border}`,
                                  background: style.bg,
                                }}
                              >
                                {/* Rating */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    marginBottom: 16,
                                  }}
                                >
                                  <StarRating
                                    value={evalData.rating}
                                    color={style.accent}
                                  />
                                  <span
                                    style={{
                                      fontSize: 13,
                                      color: "var(--ink-700)",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {evalData.rating}/5 rating
                                  </span>
                                </div>

                                {/* Dimensions */}
                                {evalData.dimensions &&
                                  Object.keys(evalData.dimensions).length >
                                  0 && (
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                          "repeat(auto-fit, minmax(200px, 1fr))",
                                        gap: "0 24px",
                                        marginBottom: 16,
                                      }}
                                    >
                                      {Object.entries(evalData.dimensions).map(
                                        ([dim, val]) => (
                                          <DimensionBar
                                            key={dim}
                                            label={dim}
                                            value={val}
                                            accent={style.accent}
                                          />
                                        ),
                                      )}
                                    </div>
                                  )}

                                {/* Tags */}
                                {evalData.tags?.length > 0 && (
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 6,
                                      flexWrap: "wrap",
                                      marginBottom: evalData.comment ? 16 : 0,
                                    }}
                                  >
                                    {evalData.tags.map((tag) => (
                                      <span
                                        key={tag}
                                        className="badge badge-outline"
                                        style={{
                                          borderColor: style.border,
                                          color: style.accent,
                                          background: "#ffffff",
                                          padding: "4px 10px",
                                        }}
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                )}

                                {/* Comment */}
                                {evalData.comment && (
                                  <div
                                    style={{
                                      fontSize: 14,
                                      color: "var(--ink-800)",
                                      fontStyle: "italic",
                                      fontFamily: "var(--serif)",
                                      padding: "12px 16px",
                                      background: "rgba(255,255,255,0.6)",
                                      borderRadius: 8,
                                      border: `1px solid ${style.border}`,
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    “{evalData.comment}”
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Latency Footer */}
                            {msg.latency_ms > 0 && (
                              <div
                                style={{
                                  padding: "10px 20px",
                                  fontSize: 12,
                                  color: "var(--ink-500)",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  borderTop: `1px solid ${style.border}`,
                                  background: "#ffffff",
                                }}
                              >
                                <Clock size={12} /> Generated in{" "}
                                {fmtMs(msg.latency_ms)}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: 15,
                      color: "var(--ink-400)",
                      fontStyle: "italic",
                      fontFamily: "var(--serif)",
                      padding: "12px 0",
                    }}
                  >
                    No responses recorded for this prompt.
                  </div>
                )}

                {/* Missing evaluation note */}
                {!turn.evaluation && turn.responses.length > 0 && (
                  <div
                    style={{
                      marginTop: 12,
                      fontSize: 14,
                      color: "var(--ink-500)",
                      fontStyle: "italic",
                      fontFamily: "var(--serif)",
                      background: "var(--paper-2)",
                      padding: "10px 16px",
                      borderRadius: 8,
                      display: "inline-block",
                    }}
                  >
                    No evaluation submitted for this round.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
